import CopyToClipboard from "../components/CopyToClipboard"
import React from "react"

export const convertCamelCaseToTitle = (string) => {
  if (string === "company_culture") return "Company Culture & Vision"
  if (string === "location") return "Location Address"
  if (string === "compensation") return "Compensation/Budget"
  if (string === "capabilities") return "Capabilities/Experise"
  //enter input_text_text -> Input Text Text
  string = string.split("_")
  string = string.map((word) => {
    return `${word[0].toUpperCase()}${word.slice(1)}`
  })
  return string.join(" ")
}

export const makeCutDottedString = (string, length) =>
  string.length > length ? `${string.slice(0, length)}...` : string

export function stripHtml(html) {
  if (html == null || html === "") return ""

  const doc = new DOMParser().parseFromString(String(html), "text/html")
  let text = doc.body.textContent || ""

  // Handle already-escaped tags stored as literal text (e.g. &lt;p&gt;...&lt;/p&gt;)
  if (/<[^>]+>/.test(text)) {
    const nested = new DOMParser().parseFromString(text, "text/html")
    text = nested.body.textContent || ""
  }

  // Collapse spaces/tabs but keep line breaks used in resource field lists
  return text
    .replace(/[^\S\n]+/g, " ")
    .replace(/ *\n */g, "\n")
    .trim()
}

export function sanitizeDraftDescription(description) {
  if (!description) return description

  try {
    const wasString = typeof description === "string"
    const parsed = wasString ? JSON.parse(description) : description
    if (!parsed?.blocks) return description

    const sanitized = {
      ...parsed,
      blocks: parsed.blocks.map((block) => {
        const text = block.text || ""
        if (!/<[^>]+>/.test(text) && !/&lt;\/?[a-zA-Z][^&]*&gt;/.test(text)) {
          return block
        }

        return {
          ...block,
          text: stripHtml(text),
          inlineStyleRanges: [],
          entityRanges: [],
        }
      }),
    }

    return wasString ? JSON.stringify(sanitized) : sanitized
  } catch {
    return description
  }
}

const NATIONAL_RESOURCE_LABELS = [
  "URL",
  "Import Source",
  "ID",
  "Sub Type",
  "Resource Type",
  "Brief Description",
  "Eligibility Criteria",
  "Status",
  "Directory URL",
  "Crawl Method",
  "Published At",
  "Unmapped TYPE",
  "Unmapped DELIVERY FORMAT",
]

const NATIONAL_RESOURCE_HIDDEN_LABELS = new Set([
  "Import Source",
  "Crawl Method",
  "Directory URL",
  "ID",
  "Unmapped TYPE",
  "Unmapped DELIVERY FORMAT",
  "URL",
  "Published At",
])

const NATIONAL_RESOURCE_DISPLAY_ORDER = [
  "Brief Description",
  "Resource Type",
  "Sub Type",
  "Eligibility Criteria",
  "Status",
  "Published At",
  "URL",
]

export function getDraftPlainText(description) {
  if (!description) return ""

  try {
    const sanitized = sanitizeDraftDescription(description)
    const parsed =
      typeof sanitized === "string" ? JSON.parse(sanitized) : sanitized
    return (parsed?.blocks || []).map((block) => block.text || "").join("\n")
  } catch {
    return ""
  }
}

export function cleanBriefDescription(value) {
  if (!value) return ""

  return String(value)
    .replace(/\s*The post .+? appeared first on .+\.?$/i, "")
    .replace(/\s+/g, " ")
    .trim()
}

export function formatPipeSeparated(value) {
  if (value == null || value === "") return value

  return String(value)
    .split("|")
    .map((part) => part.trim())
    .filter(Boolean)
    .join(", ")
}

export function toTitleCase(value) {
  if (value == null || value === "") return value

  return String(value).replace(/\w\S*/g, (word) => {
    return `${word.charAt(0).toUpperCase()}${word.slice(1)}`
  })
}

export function formatResourcePublishedAt(value) {
  if (!value || value === "unknown") return value || ""

  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value

  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  })
}

const URL_IN_TEXT_REGEX = /https?:\/\/[^\s<]+/gi
const TRAILING_URL_PUNCTUATION_REGEX = /[.,;:!?)\]}'"]+$/
const EMAIL_IN_TEXT_REGEX =
  /[a-zA-Z0-9._%+-]+@(?:[a-zA-Z0-9-]+\.)+[a-zA-Z]{2,6}(?=[^a-zA-Z]|$|[A-Z])/g

export function splitUrlAndTrailingPunctuation(rawUrl) {
  let url = String(rawUrl || "")
  let trailing = ""

  const punctMatch = url.match(TRAILING_URL_PUNCTUATION_REGEX)
  if (punctMatch) {
    trailing = punctMatch[0]
    url = url.slice(0, -trailing.length)
  }

  return { url, trailing }
}

export function linkifyText(text, className = "goldLink") {
  if (text == null || text === "") return text

  const string = String(text)
  const nodes = []
  let lastIndex = 0
  let key = 0

  const pattern = new RegExp(
    `${URL_IN_TEXT_REGEX.source}|${EMAIL_IN_TEXT_REGEX.source}`,
    "g",
  )
  let match

  while ((match = pattern.exec(string)) !== null) {
    const raw = match[0]

    if (match.index > lastIndex) {
      nodes.push(string.slice(lastIndex, match.index))
    }

    if (/^https?:\/\//i.test(raw)) {
      const { url, trailing } = splitUrlAndTrailingPunctuation(raw)
      if (url) {
        nodes.push(
          <a
            key={`link-${key++}`}
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className={className}
          >
            {url}
          </a>,
        )
      }
      if (trailing) nodes.push(trailing)
    } else {
      nodes.push(
        <a key={`link-${key++}`} href={`mailto:${raw}`} className={className}>
          {raw}
        </a>,
      )
    }

    lastIndex = match.index + raw.length
  }

  if (lastIndex < string.length) {
    nodes.push(string.slice(lastIndex))
  }

  return nodes.length ? nodes : string
}

/**
 * Makes scraped/run-on opportunity text readable by inserting spaces and
 * paragraph breaks only. Never removes words or content.
 */
export function beautifyReadableText(text) {
  if (text == null || text === "") return ""

  const placeholders = []
  const protect = (match) => {
    const token = `§§${placeholders.length}§§`
    placeholders.push(match)
    return token
  }

  let value = String(text)
    .replace(/[ \t]+/g, " ")
    .trim()

  // Keep URLs/emails intact so domain dots are not treated as sentence breaks
  value = value.replace(/https?:\/\/[^\s<]+/gi, (match, ...args) => {
    const full = args[args.length - 1]
    const offset = args[args.length - 2]
    const token = protect(match)
    const next = full[offset + match.length]
    return next && /[A-Za-z0-9]/.test(next) ? `${token} ` : token
  })
  EMAIL_IN_TEXT_REGEX.lastIndex = 0
  value = value.replace(EMAIL_IN_TEXT_REGEX, (match, ...args) => {
    const full = args[args.length - 1]
    const offset = args[args.length - 2]
    const token = protect(match)
    const next = full[offset + match.length]
    return next && /[A-Za-z0-9]/.test(next) ? `${token} ` : token
  })

  // "157767Bid" -> "157767 Bid"
  value = value.replace(/(\d)([A-Za-z])/g, "$1 $2")
  // "Resources402" / "W265" -> "Resources 402" / "W 265"
  value = value.replace(/([A-Za-z])(\d)/g, "$1 $2")
  // "amTo" / "packageMUST" -> "am To" / "package MUST"
  value = value.replace(/([a-z])([A-Z])/g, "$1 $2")
  // "46204.This" / "above.Please" -> spaced
  value = value.replace(/([.!?])([A-Za-z*])/g, "$1 $2")
  // "to:Amber" -> "to: Amber"
  value = value.replace(/:([A-Za-z])/g, ": $1")
  // "3275ORmailed" / "ORmailed" -> "3275 OR mailed"
  value = value.replace(/([^\s])OR(?=[A-Za-z])/g, "$1 OR ")
  value = value.replace(/\bOR([A-Za-z])/g, "OR $1")
  // Keep list markers readable
  value = value.replace(/\s*\*{2,}\s*/g, "\n\n*** ")

  // Soft section breaks before common continuation phrases
  value = value.replace(
    /\s+(?=(?:Please send|To allow for|This bid|The bid package|All questions|Responses to|Bid documents:)\b)/g,
    "\n\n",
  )

  // Paragraph break after longer sentences (skip initials like "W.")
  value = value.replace(/([^\n]{60,}?[a-z]{2}[.!?])\s+(?=[A-Z*])/g, "$1\n\n")

  // Restore longest tokens first so §§10§§ is not broken by §§1§§
  for (let index = placeholders.length - 1; index >= 0; index -= 1) {
    value = value.split(`§§${index}§§`).join(placeholders[index])
  }

  // Final pass for email jammed against the next word
  EMAIL_IN_TEXT_REGEX.lastIndex = 0
  value = value.replace(EMAIL_IN_TEXT_REGEX, (match, ...args) => {
    const full = args[args.length - 1]
    const offset = args[args.length - 2]
    const next = full[offset + match.length]
    return next && /[A-Za-z]/.test(next) ? `${match} ` : match
  })

  return value
    .replace(/[ \t]+\n/g, "\n")
    .replace(/\n[ \t]+/g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim()
}

export function renderReadableText(text, className = "goldLink") {
  const beautified = beautifyReadableText(text)
  if (!beautified) return null

  const paragraphs = beautified
    .split(/\n{2,}/)
    .map((part) => part.trim())
    .filter(Boolean)

  if (paragraphs.length <= 1) {
    return linkifyText(beautified, className)
  }

  return paragraphs.map((paragraph, index) => (
    <p key={`readable-p-${index}`} style={{ margin: "0 0 12px" }}>
      {linkifyText(paragraph, className)}
    </p>
  ))
}

export function beautifyDraftDescription(description) {
  if (!description) return description

  try {
    const wasString = typeof description === "string"
    const sanitized = sanitizeDraftDescription(description)
    const parsed =
      typeof sanitized === "string" ? JSON.parse(sanitized) : sanitized
    if (!parsed?.blocks) return description

    const blocks = []
    parsed.blocks.forEach((block) => {
      const beautified = beautifyReadableText(block.text || "")
      const parts = beautified
        .split(/\n{2,}/)
        .map((part) => part.trim())
        .filter(Boolean)

      if (!parts.length) {
        blocks.push(block)
        return
      }

      parts.forEach((part, index) => {
        blocks.push({
          ...block,
          key: `${block.key || "b"}${index}`,
          text: part,
          inlineStyleRanges: [],
          entityRanges: [],
        })
      })
    })

    const result = { ...parsed, blocks }
    return wasString ? JSON.stringify(result) : result
  } catch {
    return description
  }
}

export function ensureHtmlLinksOpenInNewTab(rootEl) {
  if (!rootEl) return

  Array.from(rootEl.getElementsByTagName("a")).forEach((link) => {
    const href = link.getAttribute("href") || ""
    if (!/^https?:\/\//i.test(href)) return

    link.classList.add("goldLink")
    link.setAttribute("target", "_blank")
    link.setAttribute("rel", "noopener noreferrer")
  })

  const walker = document.createTreeWalker(rootEl, NodeFilter.SHOW_TEXT)
  const textNodes = []
  while (walker.nextNode()) {
    textNodes.push(walker.currentNode)
  }

  textNodes.forEach((node) => {
    if (!node?.nodeValue || node.parentElement?.closest("a")) return

    URL_IN_TEXT_REGEX.lastIndex = 0
    if (!URL_IN_TEXT_REGEX.test(node.nodeValue)) return

    const text = node.nodeValue
    const wrapper = document.createElement("span")
    let html = ""
    let lastIndex = 0
    let match

    URL_IN_TEXT_REGEX.lastIndex = 0
    while ((match = URL_IN_TEXT_REGEX.exec(text)) !== null) {
      const { url, trailing } = splitUrlAndTrailingPunctuation(match[0])
      html += escapeHtml(text.slice(lastIndex, match.index))
      if (url) {
        html += `<a href="${escapeAttribute(
          url,
        )}" target="_blank" rel="noopener noreferrer" class="goldLink">${escapeHtml(
          url,
        )}</a>`
      }
      html += escapeHtml(trailing)
      lastIndex = match.index + match[0].length
    }
    html += escapeHtml(text.slice(lastIndex))
    wrapper.innerHTML = html
    if (node.parentNode) {
      node.parentNode.replaceChild(wrapper, node)
    }
  })
}

function escapeHtml(value) {
  return String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
}

function escapeAttribute(value) {
  return escapeHtml(value).replace(/'/g, "&#39;")
}

export function parseNationalResourceDescription(description) {
  const text = stripHtml(getDraftPlainText(description))
  if (!text) return null

  const isNationalResource = text.includes(
    "Import Source: national_resources_sync",
  )

  if (!isNationalResource) return null

  const labelPattern = NATIONAL_RESOURCE_LABELS.map((label) =>
    label.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"),
  ).join("|")
  const regex = new RegExp(
    `(?:^|\\n)(${labelPattern}):\\s*([\\s\\S]*?)(?=(?:\\n(?:${labelPattern}):)|$)`,
    "g",
  )

  const fields = {}
  let match
  while ((match = regex.exec(text)) !== null) {
    fields[match[1]] = match[2].replace(/\s+/g, " ").trim()
  }

  if (!Object.keys(fields).length) return null

  if (fields["Brief Description"]) {
    fields["Brief Description"] = cleanBriefDescription(
      fields["Brief Description"],
    )
  }
  if (fields["Published At"]) {
    fields["Published At"] = formatResourcePublishedAt(fields["Published At"])
  }
  if (fields["Resource Type"]) {
    fields["Resource Type"] = fields["Resource Type"].replace(/\|/g, ", ")
  }
  if (fields["Sub Type"]) {
    fields["Sub Type"] = fields["Sub Type"].replace(/\|/g, ", ")
  }

  return {
    briefDescription: fields["Brief Description"] || "",
    websiteUrl: fields["URL"] || "",
    meta: NATIONAL_RESOURCE_DISPLAY_ORDER.filter(
      (label) =>
        label !== "Brief Description" &&
        !NATIONAL_RESOURCE_HIDDEN_LABELS.has(label) &&
        fields[label],
    ).map((label) => ({
      label,
      value: fields[label],
    })),
  }
}

const INSTITUTE_ARTICLE_LABELS = [
  "Organization",
  "Sub Type",
  "Target Audience",
  "Resource Type",
  "Cost",
  "Location",
  "Eligibility Criteria",
  "Training Tags",
  "Status",
  "Directory URL",
  "Crawl Method",
  "Published At",
]

const INSTITUTE_ARTICLE_HIDDEN_LABELS = new Set(["Crawl Method"])

const INSTITUTE_ARTICLE_DISPLAY_ORDER = [
  "Organization",
  "Sub Type",
  "Target Audience",
  "Resource Type",
  "Cost",
  "Location",
  "Eligibility Criteria",
  "Training Tags",
  "Status",
  "Directory URL",
  "Published At",
]

function htmlBlocksToText(html) {
  const doc = new DOMParser().parseFromString(String(html), "text/html")
  const blocks = Array.from(
    doc.body.querySelectorAll("p, div, li, h1, h2, h3, h4, h5, h6"),
  )

  if (blocks.length) {
    return blocks
      .map((el) => (el.textContent || "").replace(/\s+/g, " ").trim())
      .filter(Boolean)
      .join("\n")
  }

  return stripHtml(html)
}

export function parseInstituteArticleContent(html) {
  if (!html) return null

  const raw = String(html)
  const isNationalInstitute = /national_institutes_(?:sync|csv)/i.test(raw)
  if (!isNationalInstitute) return null

  const text = htmlBlocksToText(raw)
  if (!text) return null

  const labelPattern = INSTITUTE_ARTICLE_LABELS.map((label) =>
    label.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"),
  ).join("|")
  const regex = new RegExp(
    `(?:^|\\n)(${labelPattern}):\\s*([\\s\\S]*?)(?=(?:\\n(?:${labelPattern}):)|$)`,
    "g",
  )

  const fields = {}
  let match
  while ((match = regex.exec(text)) !== null) {
    fields[match[1]] = match[2].replace(/\s+/g, " ").trim()
  }

  if (!Object.keys(fields).length) return null

  if (fields["Published At"]) {
    fields["Published At"] = formatResourcePublishedAt(fields["Published At"])
  }

  const firstLabelIndex = text.search(
    new RegExp(`(?:^|\\n)(?:${labelPattern}):`),
  )
  let intro = firstLabelIndex >= 0 ? text.slice(0, firstLabelIndex).trim() : ""

  const commentUrlMatch = raw.match(/<!--\s*URL:\s*(https?:\/\/[^\s>]+)\s*-->/i)
  const introUrlMatch = intro.match(/https?:\/\/[^\s<]+/i)
  const websiteUrl =
    (commentUrlMatch && commentUrlMatch[1]) ||
    (introUrlMatch && introUrlMatch[0]) ||
    ""

  if (websiteUrl) {
    intro = intro
      .split(websiteUrl)
      .join("")
      .replace(/\n{2,}/g, "\n")
      .trim()
  }

  return {
    briefDescription: intro,
    websiteUrl,
    meta: INSTITUTE_ARTICLE_DISPLAY_ORDER.filter(
      (label) => !INSTITUTE_ARTICLE_HIDDEN_LABELS.has(label) && fields[label],
    ).map((label) => ({
      label,
      value: formatPipeSeparated(fields[label]),
    })),
  }
}

export function opportunutiOptimizeText(text, maxLength) {
  if (text) {
    // wtf
    let string =
      text.length &&
      JSON.parse(text).blocks.map((block) => stripHtml(block.text))
    string = string.splice(0, 3)
    return string.join(" ").length > maxLength
      ? `${string.join(" ").slice(0, maxLength)}...`
      : string.join(" ")
  }
}

export const getContact = (string) => {
  const emailRegex =
    /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
  const phoneRegex = /^([+]?[\s0-9]+)?(\d{3}|[(]?[0-9]+[)])?([-]?[\s]?[0-9])+$/

  return string.split(" ").map((word) => {
    if (
      emailRegex.test(String(word).toLowerCase()) ||
      phoneRegex.test(String(word))
    ) {
      return (
        <span key={new Date().getMilliseconds()}>
          {" "}
          <CopyToClipboard text={word} />{" "}
        </span>
      )
    } else {
      return <span> {word} </span>
    }
  })
}

export function dhm(ms) {
  let days = Math.floor(ms / (24 * 60 * 60 * 1000))
  let daysms = ms % (24 * 60 * 60 * 1000)
  let hours = Math.floor(daysms / (60 * 60 * 1000))
  let hoursms = ms % (60 * 60 * 1000)
  let minutes = Math.floor(hoursms / (60 * 1000))
  let minutesms = ms % (60 * 1000)
  let sec = Math.floor(minutesms / 1000)
  return {
    days: days,
    hours: hours,
    minutes: minutes,
    sec: sec,
  }
}

export const thousandToK = (number) => {
  if (number > 999) {
    return number.toString().slice(0, -3) + " k"
  }

  return number
}
